/**
 * Church Template Default Role Catalog.
 *
 * Canonical, template-defined roles for every church-template tenant.
 * Security is keyed on stable roleIds — display labels are cosmetic and
 * tenant-overridable via tenantRoleLabels. This module is dependency-free
 * (no Firebase, no React) so it can be consumed by app code, Cloud
 * Functions, scripts, and tests.
 *
 * Catalog permission resolution is strict: defaults + explicit grants,
 * then forbiddenPermissions force-deny. No implied expansions — in
 * particular canCheckInChildren never implies canCheckOutChildren.
 * Legacy (pre-catalog) roles keep their existing behavior in
 * usePermissions.js until migration completes.
 */

// Permission keys already enforced by firestore.rules / Cloud Function guards.
export const CORE_PERMISSION_KEYS = [
  "canManageRoles",
  "canEditProfile",
  "canManageServices",
  "canPublishServicePrograms",
  "canViewDashboard",
  "canManagePeople",
  "canManageHouseholds",
  "canManageAttendance",
  "canManageChildren",
  "canManageChildrenCheckin",
  "canCheckInChildren",
  "canCheckOutChildren",
  "canManageGiving",
  "canApproveDonations",
  "canRecordOfflineGifts",
  "canApprovePayments",
  "canRecordManualPayments",
  "canManagePayments",
  "canManageStripe",
  "canManagePayouts",
  "canManageAutomations",
  "canManageFunding",
  "canManageReceipts",
  "canEditWebsite",
  "canManageEvents",
  "canUploadMedia",
  "canManageMedia",
  "canViewReports",
  "canManageCampaigns",
  "canManageFundraising",
  "canApproveCampaigns",
];

// Additive keys introduced by the catalog. Firestore rules fail closed on
// unknown keys, so shipping these grants nothing until rules consume them.
export const EXTENDED_PERMISSION_KEYS = [
  "canManageCuisine",
  "canManageTeams",
  "canManageTeamRoster",
  "canViewMemberDirectory",
  "canViewMemberContactInfo",
  "canViewAttendance",
  "canViewCareStatus",
  "canManageCareStatus",
  "canViewPastoralNotes",
  "canManagePastoralNotes",
  "canViewPrayerRequests",
  "canManagePrayerRequests",
  "canViewGivingSummary",
  "canViewDonorGiving",
  "canManageCommunications",
  "canManageMinistries",
  "canManageMinistryRoster",
  "canViewAssignedChildren",
  "canManageChildrenWorkers",
  "canManageAnnouncements",
  "canManageOwnProfile",
  "canViewOwnGiving",
  "canViewOwnAttendance",
  "canViewOwnAssignments",
  "canViewOwnSchedule",
  "canSubmitPrayerRequests",
  "canRegisterForEvents",
  "canAccessMemberContent",
  "canAccessMemberOnboarding",
];

export const CATALOG_PERMISSION_KEYS = [
  ...CORE_PERMISSION_KEYS,
  ...EXTENDED_PERMISSION_KEYS,
];

export const SCOPE_TYPES = [
  "platform",
  "tenant",
  "ministry",
  "group",
  "children_class",
  "assigned_members",
  "own_profile",
  "public",
];

export const SENSITIVITY_LEVELS = ["platform", "critical", "high", "medium", "low", "public"];

export const ROLE_TIERS = {
  platform: { order: 0, label: "Platform (Lightly only)" },
  administration: { order: 1, label: "Administration" },
  pastoral_leadership: { order: 2, label: "Pastoral Leadership" },
  ministry_operations: { order: 3, label: "Ministry Operations" },
  membership: { order: 4, label: "Membership" },
  specialized: { order: 5, label: "Specialized" },
};

// Shared boundary sets used to build forbiddenPermissions.
const FINANCE_PERMISSIONS = [
  "canManageGiving",
  "canApproveDonations",
  "canRecordOfflineGifts",
  "canApprovePayments",
  "canRecordManualPayments",
  "canManagePayments",
  "canManageStripe",
  "canManagePayouts",
  "canManageReceipts",
  "canManageFunding",
  "canViewDonorGiving",
];

const PASTORAL_NOTE_PERMISSIONS = ["canViewPastoralNotes", "canManagePastoralNotes"];
const CARE_STATUS_PERMISSIONS = ["canViewCareStatus", "canManageCareStatus"];
const CHILDREN_PERMISSIONS = [
  "canManageChildren",
  "canManageChildrenCheckin",
  "canCheckInChildren",
  "canCheckOutChildren",
  "canViewAssignedChildren",
  "canManageChildrenWorkers",
];
const PEOPLE_ADMIN_PERMISSIONS = ["canManagePeople", "canManageHouseholds"];

const MEMBER_SELF_SERVICE_PERMISSIONS = {
  canManageOwnProfile: true,
  canViewOwnGiving: true,
  canViewOwnAttendance: true,
  canSubmitPrayerRequests: true,
  canRegisterForEvents: true,
  canAccessMemberContent: true,
};

const MEMBER_FORBIDDEN_PERMISSIONS = [
  ...FINANCE_PERMISSIONS,
  "canViewGivingSummary",
  ...PASTORAL_NOTE_PERMISSIONS,
  ...CARE_STATUS_PERMISSIONS,
  ...CHILDREN_PERMISSIONS,
  ...PEOPLE_ADMIN_PERMISSIONS,
  "canViewMemberContactInfo",
  "canManageRoles",
];

function grants(keys) {
  return Object.fromEntries(keys.map((key) => [key, true]));
}

/**
 * Platform-only roles. Reserved for Lightly. Never visible or assignable in
 * a church tenant dashboard. Platform super-admin enforcement is explicitly
 * out of scope for this catalog (see docs) — these entries are reserved
 * identifiers only and grant nothing through tenant permission resolution.
 */
export const PLATFORM_ROLE_CATALOG = {
  platform_owner: {
    roleId: "platform_owner",
    defaultLabel: "Lightly Platform Owner",
    tier: "platform",
    description: "Lightly platform operator. Not a church role. Never assignable by tenants.",
    assignableByTenant: false,
    tenantCanRenameLabel: false,
    defaultPermissions: {},
    forbiddenPermissions: [],
    scopeType: "platform",
    sensitivityLevel: "platform",
    requiresBackgroundCheck: false,
    canCombineWithOtherRoles: false,
    combinationWarnings: [],
    migrationAliases: [],
    onboardingVisibility: "hidden",
  },
  platform_support_agent: {
    roleId: "platform_support_agent",
    defaultLabel: "Lightly Support Agent",
    tier: "platform",
    description: "Lightly support staff. Not a church role. Never assignable by tenants.",
    assignableByTenant: false,
    tenantCanRenameLabel: false,
    defaultPermissions: {},
    forbiddenPermissions: [],
    scopeType: "platform",
    sensitivityLevel: "platform",
    requiresBackgroundCheck: false,
    canCombineWithOtherRoles: false,
    combinationWarnings: [],
    migrationAliases: [],
    onboardingVisibility: "hidden",
  },
  partner_reseller_admin: {
    roleId: "partner_reseller_admin",
    defaultLabel: "Lightly Partner Administrator",
    tier: "platform",
    description: "Lightly partner/reseller operator. Not a church role. Never assignable by tenants.",
    assignableByTenant: false,
    tenantCanRenameLabel: false,
    defaultPermissions: {},
    forbiddenPermissions: [],
    scopeType: "platform",
    sensitivityLevel: "platform",
    requiresBackgroundCheck: false,
    canCombineWithOtherRoles: false,
    combinationWarnings: [],
    migrationAliases: [],
    onboardingVisibility: "hidden",
  },
};

export const CHURCH_ROLE_CATALOG = {
  // ── Administration ────────────────────────────────────────────────
  owner: {
    roleId: "owner",
    defaultLabel: "Owner",
    tier: "administration",
    description:
      "Full control of this church's account: settings, roles, and finances. Cuisine additionally requires enablement and an explicit grant. Limit to 1–2 trusted people.",
    assignableByTenant: true,
    tenantCanRenameLabel: false,
    defaultPermissions: grants(CATALOG_PERMISSION_KEYS.filter((key) => key !== "canManageCuisine")),
    forbiddenPermissions: [],
    scopeType: "tenant",
    sensitivityLevel: "critical",
    requiresBackgroundCheck: false,
    canCombineWithOtherRoles: false,
    combinationWarnings: ["Owner already includes every other role's access."],
    migrationAliases: ["owner"],
    onboardingVisibility: "default",
  },
  church_admin: {
    roleId: "church_admin",
    defaultLabel: "Church Administrator",
    tier: "administration",
    description:
      "Runs day-to-day operations: people, households, events, attendance, services, ministries, and communications. No giving data, no pastoral notes.",
    assignableByTenant: true,
    tenantCanRenameLabel: true,
    defaultPermissions: {
      canViewDashboard: true,
      canManagePeople: true,
      canManageHouseholds: true,
      canViewMemberDirectory: true,
      canViewMemberContactInfo: true,
      canManageTeams: true,
      canManageEvents: true,
      canManageAttendance: true,
      canViewAttendance: true,
      canManageServices: true,
      canManageMinistries: true,
      canManageCommunications: true,
      canManageAnnouncements: true,
      canViewReports: true,
    },
    forbiddenPermissions: [
      ...FINANCE_PERMISSIONS,
      "canViewGivingSummary",
      ...PASTORAL_NOTE_PERMISSIONS,
    ],
    scopeType: "tenant",
    sensitivityLevel: "high",
    requiresBackgroundCheck: false,
    canCombineWithOtherRoles: true,
    combinationWarnings: [
      "Combining Church Administrator with Finance Administrator concentrates operational and financial control in one person.",
    ],
    migrationAliases: ["admin"],
    onboardingVisibility: "default",
  },
  finance_admin: {
    roleId: "finance_admin",
    defaultLabel: "Finance Administrator",
    tier: "administration",
    description:
      "Giving and finance only: donor-level giving history, receipts and tax statements, finance reports, and payment verification. No pastoral, care, children's, or general member data.",
    assignableByTenant: true,
    tenantCanRenameLabel: true,
    defaultPermissions: {
      canViewDashboard: true,
      canManageGiving: true,
      canViewGivingSummary: true,
      canViewDonorGiving: true,
      canApproveDonations: true,
      canRecordOfflineGifts: true,
      canApprovePayments: true,
      canRecordManualPayments: true,
      canManagePayments: true,
      canManageReceipts: true,
      canViewReports: true,
    },
    forbiddenPermissions: [
      ...PASTORAL_NOTE_PERMISSIONS,
      ...CARE_STATUS_PERMISSIONS,
      ...CHILDREN_PERMISSIONS,
      ...PEOPLE_ADMIN_PERMISSIONS,
      "canViewMemberDirectory",
      "canViewMemberContactInfo",
      "canManageAttendance",
      "canViewAttendance",
      "canManageRoles",
    ],
    scopeType: "tenant",
    sensitivityLevel: "critical",
    requiresBackgroundCheck: false,
    canCombineWithOtherRoles: true,
    combinationWarnings: [
      "Finance Administrator should not be combined with pastoral, children's, or role-management roles.",
      "Any combination with Finance Administrator is flagged and audit-logged.",
    ],
    migrationAliases: ["finance_admin"],
    onboardingVisibility: "default",
  },

  // ── Pastoral Leadership ───────────────────────────────────────────
  senior_leader: {
    roleId: "senior_leader",
    defaultLabel: "Senior Pastor",
    tier: "pastoral_leadership",
    description:
      "Senior spiritual leadership: full member and household visibility, care status, pastoral notes, attendance, and all-church pastoral communications. Giving summary only if the church enables it; never itemized donor records.",
    assignableByTenant: true,
    tenantCanRenameLabel: true,
    defaultPermissions: {
      canPublishServicePrograms: true,
      canViewDashboard: true,
      canManagePeople: true,
      canManageHouseholds: true,
      canViewMemberDirectory: true,
      canViewMemberContactInfo: true,
      canViewAttendance: true,
      canManageAttendance: true,
      canViewCareStatus: true,
      canManageCareStatus: true,
      canViewPastoralNotes: true,
      canManagePastoralNotes: true,
      canViewPrayerRequests: true,
      canManagePrayerRequests: true,
      canManageCommunications: true,
      canViewReports: true,
      // canViewGivingSummary intentionally NOT default: explicit tenant-policy grant.
    },
    forbiddenPermissions: [...FINANCE_PERMISSIONS],
    scopeType: "tenant",
    sensitivityLevel: "high",
    requiresBackgroundCheck: false,
    canCombineWithOtherRoles: true,
    combinationWarnings: [
      "Combining Senior Pastor with Finance Administrator removes the separation between pastoral and financial oversight.",
    ],
    migrationAliases: [],
    onboardingVisibility: "default",
  },
  associate_leader: {
    roleId: "associate_leader",
    defaultLabel: "Associate Pastor",
    tier: "pastoral_leadership",
    description:
      "Pastoral leadership within an assigned ministry or area: member profiles, care status, and pastoral notes for that area only, plus area communications. No giving data.",
    assignableByTenant: true,
    tenantCanRenameLabel: true,
    defaultPermissions: {
      canPublishServicePrograms: true,
      canViewDashboard: true,
      canViewMemberDirectory: true,
      canViewMemberContactInfo: true,
      canViewCareStatus: true,
      canViewPastoralNotes: true,
      canManagePastoralNotes: true,
      canViewPrayerRequests: true,
      canManageCommunications: true,
    },
    forbiddenPermissions: [...FINANCE_PERMISSIONS, "canViewGivingSummary", "canManageRoles"],
    scopeType: "ministry",
    sensitivityLevel: "high",
    requiresBackgroundCheck: false,
    canCombineWithOtherRoles: true,
    combinationWarnings: [],
    migrationAliases: [],
    onboardingVisibility: "default",
  },
  pastoral_care_worker: {
    roleId: "pastoral_care_worker",
    defaultLabel: "Pastoral Care Worker",
    tier: "pastoral_leadership",
    description:
      "Elders, deacons, care team, and pastoral visitors: contact info, care status, and care notes for assigned members only, and responses to assigned prayer requests.",
    assignableByTenant: true,
    tenantCanRenameLabel: true,
    defaultPermissions: {
      canViewDashboard: true,
      canViewMemberContactInfo: true,
      canViewCareStatus: true,
      canManageCareStatus: true,
      canViewPrayerRequests: true,
    },
    forbiddenPermissions: [
      ...FINANCE_PERMISSIONS,
      "canViewGivingSummary",
      ...PEOPLE_ADMIN_PERMISSIONS,
      ...CHILDREN_PERMISSIONS,
      "canManageRoles",
    ],
    scopeType: "assigned_members",
    sensitivityLevel: "high",
    requiresBackgroundCheck: false,
    canCombineWithOtherRoles: true,
    combinationWarnings: [],
    migrationAliases: ["care_leader"],
    onboardingVisibility: "advanced",
  },

  // ── Ministry Operations ───────────────────────────────────────────
  ministry_leader: {
    roleId: "ministry_leader",
    defaultLabel: "Ministry Leader",
    tier: "ministry_operations",
    description:
      "Leads one ministry or department: its roster, events, attendance, and communications. No giving data, no pastoral notes, no other ministries.",
    assignableByTenant: true,
    tenantCanRenameLabel: true,
    defaultPermissions: {
      canPublishServicePrograms: true,
      canViewDashboard: true,
      canViewMemberDirectory: true,
      canViewMemberContactInfo: true,
      canManageMinistryRoster: true,
      canManageEvents: true,
      canUploadMedia: true,
      canViewAttendance: true,
      canManageCommunications: true,
    },
    forbiddenPermissions: [
      ...FINANCE_PERMISSIONS,
      "canViewGivingSummary",
      ...PASTORAL_NOTE_PERMISSIONS,
    ],
    scopeType: "ministry",
    sensitivityLevel: "medium",
    requiresBackgroundCheck: false,
    canCombineWithOtherRoles: true,
    combinationWarnings: [],
    migrationAliases: ["ministry_leader", "department_leader"],
    onboardingVisibility: "default",
  },
  group_leader: {
    roleId: "group_leader",
    defaultLabel: "Group Leader",
    tier: "ministry_operations",
    description:
      "Small group, house fellowship, cell, or Bible study leader: assigned group members, their contact info, group attendance and events, and submitting prayer or care concerns.",
    assignableByTenant: true,
    tenantCanRenameLabel: true,
    defaultPermissions: {
      canViewDashboard: true,
      canViewMemberDirectory: true,
      canViewMemberContactInfo: true,
      canViewAttendance: true,
      canManageEvents: true,
      canSubmitPrayerRequests: true,
    },
    forbiddenPermissions: [
      ...FINANCE_PERMISSIONS,
      "canViewGivingSummary",
      ...PASTORAL_NOTE_PERMISSIONS,
    ],
    scopeType: "group",
    sensitivityLevel: "medium",
    requiresBackgroundCheck: false,
    canCombineWithOtherRoles: true,
    combinationWarnings: [],
    migrationAliases: [],
    onboardingVisibility: "default",
  },
  children_ministry_leader: {
    roleId: "children_ministry_leader",
    defaultLabel: "Children's Ministry Leader",
    tier: "ministry_operations",
    description:
      "Oversees children's ministry: classes, approved workers, child profiles, guardian/pickup information, and check-in settings. Requires background-check eligibility. No giving data, no pastoral notes, no unrelated adult data.",
    assignableByTenant: true,
    tenantCanRenameLabel: true,
    defaultPermissions: {
      canViewDashboard: true,
      canManageChildren: true,
      canManageChildrenCheckin: true,
      canCheckInChildren: true,
      canCheckOutChildren: true,
      canManageChildrenWorkers: true,
      canViewAssignedChildren: true,
      canViewAttendance: true,
    },
    forbiddenPermissions: [
      ...FINANCE_PERMISSIONS,
      "canViewGivingSummary",
      ...PASTORAL_NOTE_PERMISSIONS,
      ...PEOPLE_ADMIN_PERMISSIONS,
    ],
    scopeType: "ministry",
    sensitivityLevel: "high",
    requiresBackgroundCheck: true,
    canCombineWithOtherRoles: true,
    combinationWarnings: [
      "Do not combine Children's Ministry Leader with Finance Administrator.",
    ],
    migrationAliases: [],
    onboardingVisibility: "advanced",
  },
  children_ministry_worker: {
    roleId: "children_ministry_worker",
    defaultLabel: "Children's Ministry Worker",
    tier: "ministry_operations",
    description:
      "Sunday school, nursery, or class worker: assigned children only, with the guardian contact and medical/allergy notes needed for safety. Check-in and check-out are separate, explicit grants. Requires background-check eligibility.",
    assignableByTenant: true,
    tenantCanRenameLabel: true,
    defaultPermissions: {
      canViewAssignedChildren: true,
      // canCheckInChildren and canCheckOutChildren are NOT defaults:
      // each must be explicitly and independently granted.
    },
    forbiddenPermissions: [
      ...FINANCE_PERMISSIONS,
      "canViewGivingSummary",
      ...PASTORAL_NOTE_PERMISSIONS,
      ...CARE_STATUS_PERMISSIONS,
      ...PEOPLE_ADMIN_PERMISSIONS,
      "canViewMemberDirectory",
      "canManageChildren",
      "canManageChildrenCheckin",
      "canManageChildrenWorkers",
      "canManageRoles",
    ],
    scopeType: "children_class",
    sensitivityLevel: "high",
    requiresBackgroundCheck: true,
    canCombineWithOtherRoles: true,
    combinationWarnings: [
      "Adding other roles to a Children's Ministry Worker may expose adult member data.",
    ],
    migrationAliases: [],
    onboardingVisibility: "advanced",
  },
  checkin_coordinator: {
    roleId: "checkin_coordinator",
    defaultLabel: "Check-In Coordinator",
    tier: "ministry_operations",
    description:
      "Service-day attendance and welcome desk: check-in dashboard, marking attendance, and the names, photos, and household grouping needed to identify people. No giving, pastoral, care, or children's private data.",
    assignableByTenant: true,
    tenantCanRenameLabel: true,
    defaultPermissions: {
      canViewDashboard: true,
      canManageAttendance: true,
      canViewAttendance: true,
      canViewMemberDirectory: true,
      // Owner decision 2026-11-02: restore the 2026-09-04 grant removed on 2026-10-07.
      // Storage rules and the vault server already allow check-in coordinators to upload.
      canUploadMedia: true,
    },
    forbiddenPermissions: [
      ...FINANCE_PERMISSIONS,
      "canViewGivingSummary",
      ...PASTORAL_NOTE_PERMISSIONS,
      ...CARE_STATUS_PERMISSIONS,
      "canManageChildren",
      "canManageChildrenWorkers",
      "canViewAssignedChildren",
      "canCheckOutChildren",
      "canManageRoles",
    ],
    scopeType: "tenant",
    sensitivityLevel: "medium",
    requiresBackgroundCheck: false,
    canCombineWithOtherRoles: true,
    combinationWarnings: [],
    migrationAliases: ["attendance_admin"],
    onboardingVisibility: "default",
  },

  // ── Membership ────────────────────────────────────────────────────
  member: {
    roleId: "member",
    defaultLabel: "Member",
    tier: "membership",
    description:
      "Registered church member: own profile, own giving, own attendance, prayer requests, event registration, and member-safe content. No one else's private data.",
    assignableByTenant: true,
    tenantCanRenameLabel: true,
    defaultPermissions: { ...MEMBER_SELF_SERVICE_PERMISSIONS },
    forbiddenPermissions: [...MEMBER_FORBIDDEN_PERMISSIONS],
    scopeType: "own_profile",
    sensitivityLevel: "low",
    requiresBackgroundCheck: false,
    canCombineWithOtherRoles: true,
    combinationWarnings: [],
    migrationAliases: ["viewer"],
    onboardingVisibility: "default",
  },
  new_member: {
    roleId: "new_member",
    defaultLabel: "New Member",
    tier: "membership",
    description:
      "Newcomer in onboarding: everything a Member can do, plus the welcome/onboarding flow and profile-completion prompts.",
    assignableByTenant: true,
    tenantCanRenameLabel: true,
    defaultPermissions: {
      ...MEMBER_SELF_SERVICE_PERMISSIONS,
      canAccessMemberOnboarding: true,
    },
    forbiddenPermissions: [...MEMBER_FORBIDDEN_PERMISSIONS],
    scopeType: "own_profile",
    sensitivityLevel: "low",
    requiresBackgroundCheck: false,
    canCombineWithOtherRoles: true,
    combinationWarnings: [],
    migrationAliases: [],
    onboardingVisibility: "advanced",
  },
  guest: {
    roleId: "guest",
    defaultLabel: "Guest",
    tier: "membership",
    description:
      "Visitor or unregistered public user: public content, service times, public events, guest giving, and guest prayer requests if enabled. Never assigned as a dashboard role.",
    assignableByTenant: false,
    tenantCanRenameLabel: true,
    defaultPermissions: {},
    forbiddenPermissions: [...CATALOG_PERMISSION_KEYS],
    scopeType: "public",
    sensitivityLevel: "public",
    requiresBackgroundCheck: false,
    canCombineWithOtherRoles: false,
    combinationWarnings: [],
    migrationAliases: [],
    onboardingVisibility: "hidden",
  },

  // ── Specialized ───────────────────────────────────────────────────
  content_manager: {
    roleId: "content_manager",
    defaultLabel: "Content Manager",
    tier: "specialized",
    description:
      "Website, media, sermons, gallery, public events, and announcements. No member private data, no giving data.",
    assignableByTenant: true,
    tenantCanRenameLabel: true,
    defaultPermissions: {
      canViewDashboard: true,
      canEditWebsite: true,
      canEditProfile: true,
      canManageEvents: true,
      canUploadMedia: true,
      canManageMedia: true,
      canManageAnnouncements: true,
    },
    forbiddenPermissions: [
      ...FINANCE_PERMISSIONS,
      "canViewGivingSummary",
      ...PASTORAL_NOTE_PERMISSIONS,
      ...CARE_STATUS_PERMISSIONS,
      ...CHILDREN_PERMISSIONS,
      ...PEOPLE_ADMIN_PERMISSIONS,
      "canViewMemberContactInfo",
      "canManageRoles",
    ],
    scopeType: "tenant",
    sensitivityLevel: "medium",
    requiresBackgroundCheck: false,
    canCombineWithOtherRoles: true,
    combinationWarnings: [],
    migrationAliases: ["media_manager", "content_admin", "communications", "editor"],
    onboardingVisibility: "default",
  },
  prayer_coordinator: {
    roleId: "prayer_coordinator",
    defaultLabel: "Prayer Coordinator",
    tier: "specialized",
    description:
      "Coordinates the prayer team: prayer requests assigned to the team, public requests, marking requests prayed for, and prayer responses. First-name-only for private requests unless explicitly authorized. No profiles, contact info, giving, or care details.",
    assignableByTenant: true,
    tenantCanRenameLabel: true,
    defaultPermissions: {
      canViewDashboard: true,
      canViewPrayerRequests: true,
      canManagePrayerRequests: true,
    },
    forbiddenPermissions: [
      ...FINANCE_PERMISSIONS,
      "canViewGivingSummary",
      ...PASTORAL_NOTE_PERMISSIONS,
      ...CARE_STATUS_PERMISSIONS,
      ...CHILDREN_PERMISSIONS,
      ...PEOPLE_ADMIN_PERMISSIONS,
      "canViewMemberDirectory",
      "canViewMemberContactInfo",
      "canManageRoles",
    ],
    scopeType: "assigned_members",
    sensitivityLevel: "medium",
    requiresBackgroundCheck: false,
    canCombineWithOtherRoles: true,
    combinationWarnings: [],
    migrationAliases: [],
    onboardingVisibility: "advanced",
  },
  volunteer: {
    roleId: "volunteer",
    defaultLabel: "Volunteer",
    tier: "specialized",
    description:
      "Generic helper, server, hospitality, or event volunteer: own profile, own assignments, own schedule, and basic event details. No member, giving, care, pastoral, or children's data.",
    assignableByTenant: true,
    tenantCanRenameLabel: true,
    defaultPermissions: {
      canManageOwnProfile: true,
      canViewOwnAssignments: true,
      canViewOwnSchedule: true,
    },
    forbiddenPermissions: [
      ...FINANCE_PERMISSIONS,
      "canViewGivingSummary",
      ...PASTORAL_NOTE_PERMISSIONS,
      ...CARE_STATUS_PERMISSIONS,
      ...CHILDREN_PERMISSIONS,
      ...PEOPLE_ADMIN_PERMISSIONS,
      "canViewMemberContactInfo",
      "canManageRoles",
    ],
    scopeType: "own_profile",
    sensitivityLevel: "low",
    requiresBackgroundCheck: false,
    canCombineWithOtherRoles: true,
    combinationWarnings: [],
    migrationAliases: ["volunteer"],
    onboardingVisibility: "default",
  },
};

export const FULL_ROLE_CATALOG = {
  ...PLATFORM_ROLE_CATALOG,
  ...CHURCH_ROLE_CATALOG,
};

export function isPlatformRoleId(roleId) {
  return Object.prototype.hasOwnProperty.call(PLATFORM_ROLE_CATALOG, String(roleId || ""));
}

export function isChurchCatalogRoleId(roleId) {
  return Object.prototype.hasOwnProperty.call(CHURCH_ROLE_CATALOG, String(roleId || ""));
}

export function getRoleDefinition(roleId) {
  return FULL_ROLE_CATALOG[String(roleId || "")] || null;
}

export function listTenantAssignableRoles() {
  return Object.values(CHURCH_ROLE_CATALOG).filter((role) => role.assignableByTenant);
}

export function listTenantVisibleRolesByTier() {
  const tiers = new Map();
  Object.values(CHURCH_ROLE_CATALOG)
    .filter((role) => role.assignableByTenant)
    .forEach((role) => {
      if (!tiers.has(role.tier)) {
        tiers.set(role.tier, { tier: role.tier, label: ROLE_TIERS[role.tier]?.label || role.tier, roles: [] });
      }
      tiers.get(role.tier).roles.push(role);
    });
  return [...tiers.values()].sort(
    (a, b) => (ROLE_TIERS[a.tier]?.order ?? 99) - (ROLE_TIERS[b.tier]?.order ?? 99)
  );
}

export const SENSITIVE_ROLE_IDS = Object.values(FULL_ROLE_CATALOG)
  .filter((role) => role.sensitivityLevel === "critical" || role.sensitivityLevel === "high")
  .map((role) => role.roleId);

export function roleRequiresSensitiveWarning(roleId) {
  return SENSITIVE_ROLE_IDS.includes(String(roleId || ""));
}

/**
 * Strict catalog permission resolution: defaults + explicit grants, then
 * forbiddenPermissions force-deny. No implied expansions of any kind.
 */
export function resolveCatalogRolePermissions(roleId, explicitPermissions = {}) {
  const definition = getRoleDefinition(roleId);
  const base = Object.fromEntries(CATALOG_PERMISSION_KEYS.map((key) => [key, false]));
  if (!definition || isPlatformRoleId(roleId)) {
    return base; // Unknown and platform roles resolve to nothing at tenant level.
  }
  const merged = { ...base, ...definition.defaultPermissions };
  Object.entries(explicitPermissions || {}).forEach(([key, value]) => {
    if (value === true && Object.prototype.hasOwnProperty.call(base, key)) {
      merged[key] = true;
    }
  });
  definition.forbiddenPermissions.forEach((key) => {
    merged[key] = false;
  });
  return merged;
}

// ── Tenant label overrides ───────────────────────────────────────────
// Stored separately from role definitions at
// organizations/{orgId}/roleLabels/{roleId} => { orgId, roleId, label, updatedAt, updatedBy }.
// Security checks always use roleId; labels are display-only.

const MAX_LABEL_LENGTH = 60;

export function resolveRoleLabel(roleId, tenantLabels = {}) {
  const definition = getRoleDefinition(roleId);
  if (!definition) return "";
  if (definition.tenantCanRenameLabel) {
    const override = String(tenantLabels?.[definition.roleId]?.label ?? tenantLabels?.[definition.roleId] ?? "").trim();
    if (override) return override.slice(0, MAX_LABEL_LENGTH);
  }
  return definition.defaultLabel;
}

export function validateTenantRoleLabelOverride({ roleId, label } = {}) {
  const definition = getRoleDefinition(roleId);
  const cleanLabel = String(label || "").trim();
  if (!definition) return { valid: false, error: "Unknown roleId." };
  if (isPlatformRoleId(roleId)) {
    return { valid: false, error: "Platform roles are not visible to tenants and cannot be renamed." };
  }
  if (!definition.tenantCanRenameLabel) {
    return { valid: false, error: `The "${definition.defaultLabel}" role label cannot be renamed.` };
  }
  if (!cleanLabel) return { valid: false, error: "Label cannot be empty." };
  if (cleanLabel.length > MAX_LABEL_LENGTH) {
    return { valid: false, error: `Label must be ${MAX_LABEL_LENGTH} characters or fewer.` };
  }
  return { valid: true, error: "" };
}

/** Snapshot for role-audit entries: always records roleId + label-at-assignment. */
export function buildRoleAuditLabelSnapshot(roleId, tenantLabels = {}) {
  return {
    roleId: String(roleId || ""),
    displayLabel: resolveRoleLabel(roleId, tenantLabels) || String(roleId || ""),
  };
}
